import { useContext, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { useBiometricEnrollment } from '../../../assets/hooks/useBiometricEnrollment';
import {
  BiometricIcon,
  RightArrowIcon,
  ShieldIcon,
} from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import '../profileSubpage.css';
import BiometricSecuritySheet from './BiometricSecuritySheet';
import './ProfileSecurity.css';

function ProfileSecurity() {
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const { profile, t } = useContext(TranslationContext);
  const { securityPage } = profile;
  const biometricEnrollment = useBiometricEnrollment();
  const [isBiometricSheetOpen, setIsBiometricSheetOpen] = useState(false);
  const stages = currentUser?.reg_data?.stages || {};
  const isPasswordSet = stages.is_password_set === true;
  const shouldShowBiometric = biometricEnrollment.availability.isAvailable;
  const biometricValue = biometricEnrollment.availability.isEnabled
    ? securityPage.enabled
    : biometricEnrollment.availability.shouldFinishSetup
      ? securityPage.finishSetup
      : biometricEnrollment.availability.shouldOpenSettings
        ? securityPage.openTelegramSettings
        : t('profile.biometric.enableSpecific', {
          label: biometricEnrollment.biometricLabel,
        });
  const openBiometricSheet = () => {
    triggerImpactHaptic('light');
    biometricEnrollment.resetEnrollment();
    setIsBiometricSheetOpen(true);
    biometricEnrollment.refreshBiometricInfo();
  };
  const openPasscodeFlow = () => {
    triggerImpactHaptic('light');
    navigate(isPasswordSet ? '/profile/security/passcode' : '/onboarding/passcode');
  };
  const closeBiometricSheet = () => {
    biometricEnrollment.resetEnrollment();
    setIsBiometricSheetOpen(false);
  };

  return (
    <section className="profile-subpage profile-security">
      <ScreenHeader title={profile.security} onBack={() => navigate('/profile')} />

      <div className="profile-subpage__content">
        <h2 className="profile-subpage__section-label profile-subpage__section-label--first">
          {securityPage.accessSection}
        </h2>
        <div className="profile-subpage__card">
          <button
            type="button"
            className="profile-subpage__row"
            onClick={openPasscodeFlow}
          >
            <ShieldIcon className="profile-security__icon" />
            <span className="profile-subpage__row-content">
              <span className="profile-subpage__row-value">
                {isPasswordSet ? securityPage.changePasscode : securityPage.setPasscode}
              </span>
              <span className="profile-subpage__row-label">{securityPage.appPasscode}</span>
            </span>
            <RightArrowIcon className="profile-security__chevron" />
          </button>

          {shouldShowBiometric && (
            <>
              <div className="profile-subpage__divider" />
              <button
                type="button"
                className="profile-subpage__row"
                onClick={openBiometricSheet}
              >
                <BiometricIcon className="profile-security__icon" />
                <span className="profile-subpage__row-content">
                  <span className="profile-subpage__row-value">{securityPage.biometric}</span>
                  <span className="profile-subpage__row-label">{biometricValue}</span>
                </span>
                <RightArrowIcon className="profile-security__chevron" />
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isBiometricSheetOpen && (
          <BiometricSecuritySheet
            enrollment={biometricEnrollment}
            onClose={closeBiometricSheet}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export default ProfileSecurity;
